import { useUpdateEventPublishedStateMutation } from '@graphql/generated';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useEventFeatureFlags } from '@shared/hooks/useEventFeatureFlags/useEventFeatureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useCallback, useMemo } from 'react';

export const useWebsitePublishCapabilityFeature = () => {
  const { eventHandle, eventInfo, loading } = useEventInfo();

  const eventFeatureFlags = useEventFeatureFlags({ eventHandle: eventHandle as string, skip: !eventHandle });
  const isEventEligible = !eventFeatureFlags.loadingEventFeatureFlags && eventFeatureFlags.isEventFeatureFlagEnabled('useWebsitePublishedState');

  const featureValue = useFeatureValue('websitePublishCapabilityEnabled', {
    skip: !isEventEligible,
  });

  const [mutate, { loading: isUpdatingEventPublished }] = useUpdateEventPublishedStateMutation();

  const updateEventPublished = useCallback(
    async (isPublished: boolean) => {
      if (!eventInfo?.eventId) {
        return;
      }

      return mutate({
        variables: {
          payload: {
            eventId: eventInfo.eventId,
            isPublished,
          },
        },
      });
    },
    [eventInfo, mutate],
  );

  const eventExposure = useMemo(
    () => ({
      value: featureValue.value === 'on',
      isLoading: featureValue.loading,
    }),
    [featureValue.loading, featureValue.value],
  );

  const eventPublishedState = useMemo(
    () => ({
      value: Boolean(eventInfo?.published),
      update: updateEventPublished,
      isLoading: loading,
      isUpdating: isUpdatingEventPublished,
    }),
    [eventInfo?.published, loading, updateEventPublished, isUpdatingEventPublished],
  );

  return {
    eventExposure,
    eventPublishedState,
  };
};
